const DEFAULT_API_URL =
  (import.meta.env.DEV ? import.meta.env.VITE_DEV_API_URL : undefined) ??
  (import.meta.env.VITE_API_URL as string) ??
  "https://web.peyphone.com/api";

/**
 * Derive Autumn / January public base URLs from the API base (…/api → …/autumn or …/january).
 * Avoids shipping Stoat CDN/proxy defaults when only VITE_API_URL is set.
 */
function siblingServiceFromApiBase(
  apiBase: string,
  suffix: "autumn" | "january",
): string {
  const base = apiBase.trim().replace(/\/+$/, "");
  if (base.endsWith("/api")) {
    return `${base.slice(0, -"/api".length)}/${suffix}`;
  }
  return `${base}/${suffix}`;
}

/** Canonical public web app origin (no trailing slash), for invite links etc. */
const PUBLIC_APP_ORIGIN =
  (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined)?.replace(
    /\/$/,
    "",
  ) ?? "";

export default {
  /**
   * Whether to emit additional debug information
   */
  DEBUG: import.meta.env.DEV || true,
  /**
   * What API server to connect to by default.
   */
  DEFAULT_API_URL,
  /**
   * Canonical base URL of the web client (for /invite/..., links). Empty in SSR; use window.location.origin in browser when empty.
   */
  PUBLIC_APP_ORIGIN,
  /**
   * Whether this is the PeyPhone production / official web instance (feature flags, discover, etc.).
   */
  IS_PEYPHONE: [
    "https://api.revolt.chat",
    "https://beta.revolt.chat/api",
    "https://revolt.chat/api",
    "https://stoat.chat/api",
    "https://web.peyphone.com/api",
  ].includes(DEFAULT_API_URL),
  /**
   * What WS server to connect to by default.
   */
  DEFAULT_WS_URL:
    (import.meta.env.DEV ? import.meta.env.VITE_DEV_WS_URL : undefined) ??
    (import.meta.env.VITE_WS_URL as string) ??
    "wss://web.peyphone.com/events",
  /**
   * What media server to connect to by default.
   */
  DEFAULT_MEDIA_URL:
    (import.meta.env.DEV ? import.meta.env.VITE_DEV_MEDIA_URL : undefined) ??
    (import.meta.env.VITE_MEDIA_URL as string) ??
    siblingServiceFromApiBase(DEFAULT_API_URL, "autumn"),
  /**
   * What proxy server to connect to by default (January: external image/video proxy).
   */
  DEFAULT_PROXY_URL:
    (import.meta.env.DEV ? import.meta.env.VITE_DEV_PROXY_URL : undefined) ??
    (import.meta.env.VITE_PROXY_URL as string) ??
    siblingServiceFromApiBase(DEFAULT_API_URL, "january"),
  /**
   * Base URL for default Unicode emoji CDN (path /emoji/{pack}/... appended in client).
   */
  DEFAULT_EMOJI_CDN:
    (import.meta.env.VITE_EMOJI_CDN as string | undefined) ??
    "https://static.stoat.chat",
  /**
   * hCaptcha site key to use if enabled
   */
  HCAPTCHA_SITEKEY: import.meta.env.VITE_HCAPTCHA_SITEKEY as string,
  /**
   * Maximum number of replies a message can have
   */
  MAX_REPLIES: (import.meta.env.VITE_CFG_MAX_REPLIES as number) ?? 5,
  /**
   * Maximum number of attachments a message can have
   */
  MAX_ATTACHMENTS: (import.meta.env.VITE_CFG_MAX_ATTACHMENTS as number) ?? 5,
  /**
   * Maximum number of emoji a server can have
   */
  MAX_EMOJI: (import.meta.env.VITE_CFG_MAX_EMOJI as number) ?? 100,
  /**
   * Max file size allowed for uploads (in bytes)
   * 20 MB = 20 * 1024 * 1024 = 20,971,520 bytes
   * I kinda wonder if this should be a setting, or something fetched from the backend dynamically.
   */
  MAX_FILE_SIZE:
    (import.meta.env.VITE_CFG_MAX_FILE_SIZE as number) ?? 20_000_000,
  /**
   * Session ID to set during development.
   */
  DEVELOPMENT_SESSION_ID: import.meta.env.DEV
    ? (import.meta.env.VITE_SESSION_ID as string)
    : undefined,
  /**
   * Token to set during development.
   */
  DEVELOPMENT_TOKEN: import.meta.env.DEV
    ? (import.meta.env.VITE_TOKEN as string)
    : undefined,
  /**
   * User ID to set during development.
   */
  DEVELOPMENT_USER_ID: import.meta.env.DEV
    ? (import.meta.env.VITE_USER_ID as string)
    : undefined,
};
